import React, { useState } from "react";
import { Search, MessageCircle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";

const counties = [
  "Dublin", "Cork", "Galway", "Limerick", "Waterford", "Wexford", "Wicklow",
  "Louth", "Kildare", "Meath", "Westmeath", "Offaly", "Laois", "Carlow",
  "Kilkenny", "Tipperary", "Kerry", "Clare", "Mayo", "Sligo", "Roscommon",
  "Longford", "Cavan", "Monaghan", "Donegal", "Leitrim",
];

export default function HeroSection() {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const navigate = useNavigate();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.length > 1) {
      setSuggestions(counties.filter((c) => c.toLowerCase().includes(query.toLowerCase())));
    } else {
      setSuggestions([]);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery) return;
    const match = counties.find((c) => c.toLowerCase() === searchQuery.toLowerCase());
    if (match) return navigate(`/nursing-homes/${match.toLowerCase()}`);
    if (suggestions.length > 0) return navigate(`/nursing-homes/${suggestions[0].toLowerCase()}`);
    navigate("/nursing-homes");
  };

  const selectSuggestion = (county: string) => {
    setSearchQuery(county);
    setSuggestions([]);
    navigate(`/nursing-homes/${county.toLowerCase()}`);
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[hsl(var(--warm-cream))] via-background to-white">
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[hsl(var(--green-glow))] opacity-20 blur-3xl -translate-y-1/2 translate-x-1/3" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[hsl(var(--secondary))] opacity-30 blur-3xl translate-y-1/2 -translate-x-1/3" />

      <div className="container relative z-10 pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="max-w-3xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 backdrop-blur px-4 py-1.5 mb-8"
          >
            <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
            <span className="text-sm font-medium text-muted-foreground">
              Trusted by families across Ireland
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="hero-text mb-6"
          >
            You're not alone in finding{" "}
            <span className="text-primary">the right care</span> for your
            loved one
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Get free, instant guidance on Fair Deal applications, care
            assessments, and finding the right nursing home — from people who
            genuinely understand what you're going through.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base shadow-lg shadow-primary/20"
              onClick={() => navigate("/consultations")}
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              Chat with Care Advisor
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-border text-foreground hover:bg-accent px-8 py-6 text-base"
              onClick={() => navigate("/nursing-homes")}
            >
              Browse Nursing Homes
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </motion.div>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative max-w-xl mx-auto"
          >
            <form onSubmit={handleSearchSubmit} className="relative">
              <Input
                type="text"
                placeholder="Search nursing homes by county…"
                value={searchQuery}
                onChange={handleSearchChange}
                className="pr-12 py-6 text-base bg-card shadow-md border-border focus-visible:ring-primary/30"
              />
              <Button
                type="submit"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-primary hover:bg-primary/90"
              >
                <Search className="h-4 w-4" />
              </Button>
            </form>

            {suggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-card rounded-lg shadow-lg border border-border">
                <ul className="py-1">
                  {suggestions.map((county) => (
                    <li
                      key={county}
                      className="px-4 py-2.5 cursor-pointer hover:bg-accent transition-colors text-sm"
                      onClick={() => selectSuggestion(county)}
                    >
                      {county}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <span className="text-sm text-muted-foreground">Popular:</span>
              {["Dublin", "Cork", "Galway", "Kildare"].map((county) => (
                <button
                  key={county}
                  className="text-sm text-primary font-medium hover:underline underline-offset-2"
                  onClick={() => navigate(`/nursing-homes/${county.toLowerCase()}`)}
                >
                  {county}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
